<script lang="ts">
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import _ from "@/boot/lodash";
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
import { DataModules } from "@/store/modules/data/modules";
import { OrgConfigKeys, QUERY_PARAMS } from "@/data/constants";
import { DeferModes } from "@/data/enums/provisioning";
import type { PropType, CreateElement } from "vue";
import type {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import type { IBasketProvisioningFieldsForm } from "@/models/provisioning";
import type { IClient } from "@/models/clients";
import type { IBasket } from "@/models/baskets";
import type { IProduct } from "@/models/products";

export default defineComponent({
  name: "ProvisioningProvider",
  mixins: [RequestPromises, Context],
  provide() {
    return {
      provisionProviderData: this
        .provisionProviderData as IProvisionProviderData,
      provisionProviderMethods: this
        .provisionProviderMethods as IProvisionProviderMethods
    };
  },
  props: {
    handleLoading: { type: Boolean, default: false },
    loaderAttrs: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({})
    },
    handleRequestError: { type: Boolean, default: true }
  },
  data: () => ({
    isLoading: false,
    isReloading: false,
    hasRequestError: false,
    isProcessing: false,
    provisionErrors: {},
    savedBasketProductValues: {}
  }),
  computed: {
    ...mapGetters({
      scope: "data/provisioning/basketProducts/scope",
      productScope: "data/provisioning/basketProducts/productScope",
      valuesScope: "data/provisioning/basketProducts/valuesScope"
    }),
    hasProvisioning() {
      return this.$store.getters["org/hasProvisioningEnabled"];
    },
    pathBasketProductId() {
      return _.get(this.$route, `query.${QUERY_PARAMS.BASKET_PRODUCT_ID}`, "");
    },
    provisionProviderMethods(): IProvisionProviderMethods {
      return {
        basketProductFields: this.basketProductFields,
        basketProductFieldsValues: this.basketProductFieldsValues,
        checkConfiguration: this.checkConfiguration,
        checkProvisionConfiguration: this.checkProvisionConfiguration,
        destroyBasketProductFields: this.destroyBasketProductFields,
        destroyFieldsValues: this.destroyFieldsValues,
        destroyProductFields: this.destroyProductFields,
        getBasketProductFields: this.getBasketProductFields,
        getFieldsValues: this.getFieldsValues,
        getOrgProvisioning: this.getOrgProvisioning,
        getProductFields: this.getProductFields,
        isProvisionable: this.isProvisionable,
        productFields: this.productFields,
        reloadData: this.reloadData,
        removeBasketProductValues: this.removeBasketProductValues,
        saveBasketProductValues: this.saveBasketProductValues,
        updateBasketProductFields: this.updateBasketProductFields,
        quickUpdateBasketProductFields: this.quickUpdateBasketProductFields
      };
    }
  },
  async created() {
    this.isLoading = true;
    await this.loadData();
  },
  methods: {
    async loadData() {
      try {
        await this.getOrgProvisioning(this.isReloading);
        this.hasRequestError = false;
      } catch (error) {
        this.hasRequestError = true;
        this.$store.dispatch("api/handleError", error);
      } finally {
        this.isLoading = false;
        this.isReloading = false;
      }
    },
    reloadData() {
      this.isReloading = true;
      return this.loadData();
    },
    getOrgProvisioning(ignoreStored = false) {
      return this.$store.dispatch("org/getConfig", {
        ignoreStored,
        keys: [OrgConfigKeys.PRODUCT_PROVISIONING_ENABLED]
      });
    },
    getBasketProductFields(
      basketId: IBasket["id"],
      productId: IProduct["id"],
      subProductIds: IProduct["id"][] | null = null,
      ignoreStored = false
    ) {
      return this.$store.dispatch(
        `data/${DataModules.PROVISIONING_BASKET_PRODUCTS}/listFields`,
        {
          basketId,
          productId,
          subProductIds,
          scope: this.scope(basketId, productId),
          vm: this,
          ignoreStored
        }
      );
    },
    getProductFields(
      productId: IProduct["id"],
      clientId: IClient["id"],
      subProductIds: IProduct["id"][] | null = null
    ) {
      return this.$store.dispatch(
        `data/${DataModules.PROVISIONING_BASKET_PRODUCTS}/listProductFields`,
        _.merge(
          {
            productId,
            subProductIds,
            scope: this.productScope(productId),
            vm: this
          },
          clientId ? { clientId } : {}
        )
      );
    },
    destroyBasketProductFields(
      basketId: IBasket["id"],
      productId: IProduct["id"]
    ) {
      this.$store.dispatch("data/binList", {
        storeModule: DataModules.PROVISIONING_BASKET_PRODUCTS,
        scope: this.scope(basketId, productId),
        vm: this
      });
    },
    destroyProductFields(productId: IProduct["id"]) {
      this.$store.dispatch("data/binList", {
        storeModule: DataModules.PROVISIONING_BASKET_PRODUCTS,
        scope: this.productScope(productId),
        vm: this
      });
    },
    getFieldsValues(
      basketId: IBasket["id"],
      productId: IProduct["id"],
      subProductIds: IProduct["id"][] | null = null,
      ignoreStored = false
    ) {
      return this.$store.dispatch(
        `data/${DataModules.PROVISIONING_BASKET_PRODUCTS}/getFieldsValues`,
        {
          basketId,
          productId,
          subProductIds,
          scope: this.valuesScope(basketId, productId),
          vm: this,
          ignoreStored
        }
      );
    },
    destroyFieldsValues(basketId: IBasket["id"], productId: IProduct["id"]) {
      this.$store.dispatch("data/binList", {
        storeModule: DataModules.PROVISIONING_BASKET_PRODUCTS,
        scope: this.valuesScope(basketId, productId),
        vm: this
      });
    },
    basketProductFields(basketId: IBasket["id"], productId: IProduct["id"]) {
      return _.get(
        this.$store,
        `state.data.provisioning.basketProducts.${this.scope(
          basketId,
          productId
        )}.data`
      );
    },
    productFields(productId: IProduct["id"]) {
      return _.get(
        this.$store,
        `state.data.provisioning.basketProducts.${this.productScope(
          productId
        )}.data`
      );
    },
    basketProductFieldsValues(
      basketId: IBasket["id"],
      productId: IProduct["id"]
    ) {
      return _.get(
        this.$store,
        `state.data.provisioning.basketProducts.${this.valuesScope(
          basketId,
          productId
        )}.data`
      );
    },
    async checkConfiguration(basketId: IBasket["id"]) {
      try {
        await this.checkProvisionConfiguration(basketId);
        this.provisionErrors = {};
        return Promise.resolve();
      } catch (error) {
        // Type 666 means special privisioning error which we need to map as 422, special type added by Harry :)
        if (
          !this.$store.getters["api/is"](error, 409) ||
          _.get(error, "response.data.error.type") !== 666
        ) {
          throw error;
        }

        this.provisionErrors = _.get(error, "response.data.error");
        return Promise.resolve();
      }
    },
    async isProvisionable({
      productId,
      clientId,
      pfields = []
    }: {
      productId: IProduct["id"];
      clientId: IClient["id"];
      pfields?: { name: string; value: string }[];
    }) {
      if (!this.hasProvisioning) return false;

      let provisionFields: any[] = [];

      await this.getProductFields(productId, clientId);
      provisionFields = _.filter(
        _.map(this.productFields(productId), value => value),
        field => {
          const pfieldValue = _.get(
            _.find(pfields, ["name", field.name]),
            "value",
            null
          );
          return (
            field.required &&
            field.defer_mode !== DeferModes.HIDDEN &&
            _.isNil(field.default_value) &&
            _.isNil(pfieldValue)
          );
        }
      );
      this.destroyProductFields(productId);

      return !!provisionFields.length;
    },
    checkProvisionConfiguration(basketId: IBasket["id"]) {
      return this.$store.dispatch(
        `data/${DataModules.PROVISIONING_BASKET_PRODUCTS}/checkConfiguration`,
        {
          basketId,
          vm: this,
          ignoreStored: true
        }
      );
    },
    saveBasketProductValues({ basketId, productId, data }) {
      this.savedBasketProductValues[`${basketId}_${productId}`] = data;
    },
    removeBasketProductValues({ basketId, productId }) {
      if (!_.has(this.savedBasketProductValues, `${basketId}_${productId}`))
        return;
      delete this.savedBasketProductValues[`${basketId}_${productId}`];
    },
    async updateBasketProductFields(
      basketId: IBasket["id"],
      productId: IProduct["id"],
      data: IBasketProvisioningFieldsForm,
      ignoreValidate = false
    ) {
      try {
        await this.$store.dispatch(
          `data/${DataModules.PROVISIONING_BASKET_PRODUCTS}/updateValues`,
          {
            basketId,
            productId,
            data: {
              ...data,
              ...(ignoreValidate ? { validate: 0 } : {})
            }
          }
        );
      } catch (error) {
        if (ignoreValidate) return Promise.resolve();

        this.saveBasketProductValues({
          basketId,
          productId,
          data: {
            ...data.provision_field_values,
            error
          }
        });

        return Promise.reject(error);
      }
    },
    async quickUpdateBasketProductFields({
      basketId,
      productId,
      basketProductId,
      clientId,
      pfields = [],
      ignoreValidate = false
    }: {
      basketId: IBasket["id"];
      productId: IProduct["id"];
      basketProductId: IProduct["id"];
      clientId: IClient["id"];
      pfields?: { name: string; value: string }[];
      ignoreValidate?: boolean;
    }) {
      if (!this.hasProvisioning) return Promise.resolve();

      await this.getProductFields(productId, clientId);
      const provisionFields = this.productFields(productId);
      this.destroyProductFields(productId);

      const form = {};

      _.forEach(
        _.filter(
          _.map(provisionFields, value => value),
          field => {
            const pfieldValue = _.get(
              _.find(pfields, ["name", field.name]),
              "value",
              null
            );

            return (
              field.required &&
              field.defer_mode !== DeferModes.HIDDEN &&
              (!_.isNull(pfieldValue) || !_.isNil(field.default_value))
            );
          }
        ),
        field => {
          form[field.name] = _.get(
            _.find(pfields, ["name", field.name]),
            "value",
            field.default_value
          );
        }
      );

      if (_.isEmpty(form)) return Promise.resolve();

      return this.updateBasketProductFields(
        basketId,
        basketProductId,
        {
          provision_field_values: form
        },
        ignoreValidate
      );
    },
    clearProvisionErrors() {
      this.provisionErrors = {};
    },
    provisionProviderData(): IProvisionProviderData {
      return {
        hasProvisioning: this.hasProvisioning,
        hasRequestError: this.hasRequestError,
        isLoading: this.isLoading,
        isProcessing: this.isProcessing,
        isReloading: this.isReloading,
        pathBasketProductId: this.pathBasketProductId,
        provisionErrors: this.provisionErrors,
        savedBasketProductValues: this.savedBasketProductValues
      };
    }
  },
  render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", {
        props: { if: true },
        attrs: this.loaderAttrs
      });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isLoading && this.hasRequestError },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $provisionData: this.provisionProviderData(),
        $provisionMethods: this.provisionProviderMethods
      })
    );
  }
});
</script>
